import { useState } from 'react';

import { Button, Card, OptionGroup, OptionRow, StatusPill } from '@/design-system';
import {
    ROLLOUT_MAX_TENANTS,
    ROLLOUT_STATUS_TONES,
    TENANT_STATUS_LABELS,
    type PlatformTemplate,
    type PlatformTenant,
} from '@/entities/tenant';
import { errorMessage } from '@/shared/api';
import { formatDayLong } from '@/shared/lib';

import { AdminHeader } from './components/AdminHeader';
import { AdminRegion } from './components/AdminRegion';
import { adminCopy } from './copy';
import { usePlatformTemplates, usePlatformTenants, useRollOutTemplate } from './hooks';

/**
 * Platformbeheer - de vragenlijsten van het platform, en ze uitrollen naar
 * bedrijven (`POST /platform/templates/rollout`).
 *
 * Uitrollen is idempotent op de inhoud, niet op het bedrijf: een bedrijf met een
 * oudere versie wordt bijgewerkt, een bedrijf dat al bij is wordt overgeslagen.
 * Per bedrijf staat de uitkomst erbij, met de reden als het mislukt of als het
 * bedrijf met rust is gelaten.
 */
export function AdminTemplatesPage() {
    const templates = usePlatformTemplates();
    const tenants = usePlatformTenants({ page: 1 });

    return (
        <div className="flex min-h-0 flex-1 flex-col">
            <AdminHeader
                eyebrow={`${adminCopy.eyebrowPlatform} · ${adminCopy.templatesEyebrow}`}
                title={adminCopy.templatesTitle}
            />

            <div className="flex flex-1 flex-col gap-[14px] overflow-auto bg-[var(--surface-page)] px-7 pt-[22px] pb-8">
                <p className="m-0 max-w-[720px] text-[13px] leading-[1.5] text-[var(--text-secondary)]">
                    {adminCopy.templatesIntro}
                </p>

                <AdminRegion
                    title={adminCopy.templatesTitle}
                    errorTitle={adminCopy.templatesError}
                    query={templates}
                >
                    {templates.data && templates.data.length === 0 ? (
                        <Card padding="md">
                            <p className="m-0 text-[13px] text-[var(--text-tertiary-safe)]">
                                {adminCopy.templatesEmpty}
                            </p>
                        </Card>
                    ) : null}
                    {templates.data?.map((template) => (
                        <TemplateCard
                            key={template.uuid}
                            template={template}
                            tenants={tenants.data?.items ?? []}
                        />
                    ))}
                </AdminRegion>
            </div>
        </div>
    );
}

function TemplateCard({
    template,
    tenants,
}: {
    template: PlatformTemplate;
    tenants: readonly PlatformTenant[];
}) {
    const [open, setOpen] = useState(false);
    const [chosen, setChosen] = useState<string[]>([]);
    const rollOut = useRollOutTemplate();
    const tooMany = chosen.length > ROLLOUT_MAX_TENANTS;
    const results = rollOut.data ?? [];
    const nameOf = (uuid: string) => tenants.find((tenant) => tenant.uuid === uuid)?.name ?? uuid;
    const facts = [
        template.norm,
        template.versionNumber === null ? null : adminCopy.templateVersion(template.versionNumber),
        template.publishedAt
            ? adminCopy.templatePublished(formatDayLong(template.publishedAt))
            : null,
    ].filter((fact): fact is string => fact !== null);

    return (
        <Card padding="md" as="section" aria-labelledby={`template-${template.uuid}`}>
            <div className="flex flex-wrap items-center justify-between gap-3">
                <div className="min-w-0">
                    <h2
                        id={`template-${template.uuid}`}
                        className="m-0 font-[family-name:var(--font-display)] text-[15px] leading-[1.3] font-semibold text-[var(--text-primary)]"
                    >
                        {template.name}
                    </h2>
                    <p className="m-0 mt-[2px] text-[12px] leading-[1.4] text-[var(--text-tertiary-safe)]">
                        {facts.join(' · ')}
                    </p>
                </div>
                {open ? null : (
                    <Button variant="outline" size="sm" onClick={() => setOpen(true)}>
                        {adminCopy.rollout}
                    </Button>
                )}
            </div>

            {open ? (
                <div className="mt-3 border-t border-[var(--border-subtle)] pt-3">
                    <p className="m-0 text-[12px] leading-[1.4] text-[var(--text-secondary)]">
                        {adminCopy.rolloutPick(ROLLOUT_MAX_TENANTS)}
                    </p>
                    <div className="mt-2 flex flex-wrap gap-2">
                        <Button
                            variant="ghost"
                            size="xs"
                            onClick={() => setChosen(tenants.map((tenant) => tenant.uuid))}
                        >
                            {adminCopy.rolloutAll}
                        </Button>
                        <Button variant="ghost" size="xs" onClick={() => setChosen([])}>
                            {adminCopy.rolloutNone}
                        </Button>
                    </div>

                    <OptionGroup
                        type="check"
                        label={adminCopy.rolloutTenantsLabel(template.name)}
                        className="mt-1 px-0"
                    >
                        {tenants.map((tenant, index) => (
                            <OptionRow
                                key={tenant.uuid}
                                index={index}
                                checked={chosen.includes(tenant.uuid)}
                                onSelect={(on) =>
                                    setChosen((current) =>
                                        on
                                            ? [...current, tenant.uuid]
                                            : current.filter((uuid) => uuid !== tenant.uuid),
                                    )
                                }
                            >
                                {`${tenant.name} · ${TENANT_STATUS_LABELS[tenant.status]}`}
                            </OptionRow>
                        ))}
                    </OptionGroup>

                    <div className="mt-3 flex flex-wrap items-center gap-2">
                        <Button
                            variant="primary"
                            size="sm"
                            disabled={rollOut.isPending || chosen.length === 0 || tooMany}
                            onClick={() =>
                                rollOut.mutate({ templateUuid: template.uuid, tenantUuids: chosen })
                            }
                        >
                            {rollOut.isPending
                                ? adminCopy.rolloutBusy
                                : adminCopy.rolloutSubmit(chosen.length)}
                        </Button>
                        <Button
                            variant="outline"
                            size="sm"
                            onClick={() => {
                                rollOut.reset();
                                setChosen([]);
                                setOpen(false);
                            }}
                        >
                            {adminCopy.cancel}
                        </Button>
                        {tooMany ? (
                            <span className="text-[12px] text-[var(--warn-text)]">
                                {adminCopy.rolloutTooMany(ROLLOUT_MAX_TENANTS)}
                            </span>
                        ) : null}
                    </div>

                    {rollOut.error ? (
                        <p
                            role="alert"
                            className="m-0 mt-2 text-[12px] leading-[1.4] text-[var(--danger)]"
                        >
                            {errorMessage(rollOut.error)}
                        </p>
                    ) : null}
                </div>
            ) : null}

            {results.length > 0 ? (
                <section aria-label={adminCopy.rolloutResult} className="mt-3">
                    <ul className="m-0 list-none p-0">
                        {results.map((result) => (
                            <li
                                key={result.tenantUuid}
                                className="flex flex-wrap items-center gap-x-3 gap-y-1 border-b border-[var(--border-subtle-2)] py-[8px]"
                            >
                                <span className="min-w-[160px] flex-1 text-[13px] text-[var(--text-primary)]">
                                    {nameOf(result.tenantUuid)}
                                </span>
                                <StatusPill tone={ROLLOUT_STATUS_TONES[result.status]} size="sm">
                                    {result.statusLabel}
                                </StatusPill>
                                {result.error ? (
                                    <span
                                        className="basis-full text-[12px] leading-[1.4]"
                                        style={{
                                            color:
                                                result.status === 'failed'
                                                    ? 'var(--warn-text)'
                                                    : 'var(--text-secondary)',
                                        }}
                                    >
                                        {result.error}
                                    </span>
                                ) : null}
                            </li>
                        ))}
                    </ul>
                </section>
            ) : null}
        </Card>
    );
}
